// CPB JavaScript for AU site — Optimized + Zip-safe mount (no cloning)
// Goals:
// - Zip widget shows under price
// - File B can set inline-flex on price container without trapping Zip inline
// - No double-toggle accordion bug
// - No leaked timers
// - Handles async CPB/Klaviyo rendering with retries
// - More robust price parsing (keeps cents + commas)

window.PXUTheme = window.PXUTheme || {};
window.PXUTheme.contentCreator = window.PXUTheme.contentCreator || {};

(() => {
  'use strict';

  /* -------------------------------------------
   * Accordion module (delegated, no dbl-toggle)
   * ----------------------------------------- */
  window.PXUTheme.contentCreator.accordion = window.PXUTheme.contentCreator.accordion || {
    _bound: false,
    _handleClick: null,

    init() {
      if (this._bound) return;

      const containers = document.querySelectorAll('.accordion, [data-cc-accordion]');
      if (!containers.length) return;

      containers.forEach(container => {
        container.querySelectorAll('dd').forEach(el => el.setAttribute('aria-hidden', 'true'));
        container.querySelectorAll('dt > a').forEach(el => el.setAttribute('aria-expanded', 'false'));
      });

      this._handleClick = (e) => {
        const target = e.target.closest('.accordion > dt > a, [data-cc-accordion] > dt > a');
        if (!target) return;

        e.preventDefault();

        const isExpanded = target.getAttribute('aria-expanded') === 'true';
        const content = target.parentElement?.nextElementSibling;

        target.setAttribute('aria-expanded', String(!isExpanded));
        if (content) content.setAttribute('aria-hidden', String(isExpanded));
      };

      document.addEventListener('click', this._handleClick);
      this._bound = true;
    },

    unload() {
      if (!this._bound) return;
      document.removeEventListener('click', this._handleClick);
      this._handleClick = null;
      this._bound = false;
    }
  };

  /* -------------------------------------------
   * Main CPB module
   * ----------------------------------------- */
  const CONFIG = {
    DEBUG: false,
    RETRY_DELAYS_MS: [0, 80, 200, 450, 900],
    OOS_RETRY_DELAYS_MS: [150, 500, 1200]
  };

  const state = {
    builder: null,
    panelsContainer: null,

    customPricesContainer: null,
    priceObserver: null,
    rafId: null,

    timeouts: new Set(),
    mountedId: null
  };

  const utils = {
    qs: (sel, parent = document) => parent.querySelector(sel),
    qsa: (sel, parent = document) => parent.querySelectorAll(sel),

    log(...args) {
      if (CONFIG.DEBUG) console.log('CPB:', ...args);
    },
    error(...args) {
      console.error('CPB:', ...args);
    },

    setTrackedTimeout(fn, delay) {
      const id = setTimeout(() => {
        state.timeouts.delete(id);
        fn();
      }, delay);
      state.timeouts.add(id);
      return id;
    },

    clearAllTimeouts() {
      state.timeouts.forEach(clearTimeout);
      state.timeouts.clear();
    },

    retry(fn, delays = CONFIG.RETRY_DELAYS_MS) {
      delays.forEach(delay => {
        utils.setTrackedTimeout(() => {
          try { fn(); } catch (_) {}
        }, delay);
      });
    },

    isVisible(el) {
      return !!(el && el.offsetParent !== null);
    },

    newMountId() {
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  };

  function restoreZipWidget() {
    const widget = document.getElementById('shopify-zip-prod-widget');
    const originalHost = document.querySelector('[data-zip-original-host]');
    const cpbMount = document.querySelector('.cpb-zip-widget-mount');

    if (cpbMount) cpbMount.remove();

    if (widget && originalHost && widget.parentElement !== originalHost) {
      originalHost.appendChild(widget);
    }
  }

  function restoreAfterpayPlacement() {
    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    const originalHost = document.querySelector('[data-afterpay-original-host]');
    const afterpayMount = document.querySelector('.cpb-afterpay-mount');

    if (afterpayMount) afterpayMount.remove();

    if (placement && originalHost && placement.parentElement !== originalHost) {
      originalHost.appendChild(placement);
      placement.style.display = '';
    }
  }

  function cleanup() {
    utils.clearAllTimeouts();

    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }

    if (state.priceObserver) {
      state.priceObserver.disconnect();
      state.priceObserver = null;
    }

    window.PXUTheme.contentCreator.accordion?.unload?.();
    restoreZipWidget();
    restoreAfterpayPlacement();

    state.builder = null;
    state.panelsContainer = null;
    state.customPricesContainer = null;
    state.mountedId = null;
  }

  function initGlobals() {
    state.builder = utils.qs('#product-builder');
    if (!state.builder) return false;

    state.panelsContainer = utils.qs('.cpb-panels-container', state.builder);
    return !!state.panelsContainer;
  }

  /* -------------------------------------------
   * Price formatting (commas, NO decimals)
   * ----------------------------------------- */
  const PRICE_REGEX = /(\d[\d,]*)/;
  const PRICE_DECIMAL_REGEX = /(\d[\d,]*(?:[.,]\d{1,2})?)/;

  function formatPrice(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_REGEX);
    if (!match) return '$0';

    const wholeNumber = match[1].replace(/,/g, '');
    const withCommas = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `$${withCommas}`;
  }

  function parseAmount(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_DECIMAL_REGEX);
    if (!match) return null;

    const token = match[1];
    const hasDot = token.includes('.');
    const hasComma = token.includes(',');
    let normalized = token;

    if (hasDot && hasComma) {
      const decimalSeparator = token.lastIndexOf('.') > token.lastIndexOf(',') ? '.' : ',';
      if (decimalSeparator === '.') {
        normalized = token.replace(/,/g, '');
      } else {
        normalized = token.replace(/\./g, '').replace(',', '.');
      }
    } else if (hasComma && /,\d{1,2}$/.test(token)) {
      normalized = token.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = token.replace(/,/g, '');
    }

    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : null;
  }

  function syncAfterpayAmount(priceText) {
    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return;

    const amount = parseAmount(priceText);
    if (amount === null) return;

    placement.setAttribute('data-amount', amount.toFixed(2));
  }

  function getStoreContext() {
    const host = window.location.hostname;
    if (host.includes('growplay-monkey-bars-nz.myshopify.com') || host.includes('nz.growplaymonkeybars.com')) {
      return { region: 'nz', symbol: 'NZ$' };
    }

    if (host.includes('growplay-monkey-bars-usa.myshopify.com') || host.includes('growplayusa.com')) {
      return { region: 'us', symbol: '$' };
    }

    return { region: 'au', symbol: '$' };
  }

  function syncZipAmount(priceText) {
    const widget = document.getElementById('shopify-zip-prod-widget');
    if (!widget) return;

    const amount = parseAmount(priceText);
    if (amount === null) return;

    const normalized = String(Math.round(amount));
    widget.setAttribute('data-zm-price', normalized);
    widget.setAttribute('amount', normalized);
  }

  function findBestPriceEl() {
    const prices = Array.from(utils.qsa('.cpb-product-price', state.panelsContainer));
    if (!prices.length) return null;

    const visible = prices.slice().reverse().find(utils.isVisible);
    return visible || prices[prices.length - 1];
  }

  function ensureCustomPriceContainer() {
    if (state.customPricesContainer) return true;

    const realPrice = findBestPriceEl();
    if (!realPrice) return false;

    const container = document.createElement('div');
    container.className = 'cpb-custom-prices-container';

    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'cpb-custom-product-price-container';
    priceWrapper.style.display = 'block';
    priceWrapper.style.width = '100%';

    const customPrice = document.createElement('span');
    customPrice.className = 'cpb-custom-product-price';
    customPrice.textContent = formatPrice(realPrice.textContent);

    priceWrapper.appendChild(customPrice);
    container.appendChild(priceWrapper);

    state.customPricesContainer = container;
    state.builder.dataset.customProductPrice = 'true';

    const updatePrice = () => {
      if (state.rafId) return;
      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        const currentPriceEl = findBestPriceEl() || realPrice;
        const currentText = currentPriceEl.textContent;
        customPrice.textContent = formatPrice(currentText);
        syncAfterpayAmount(currentText);
        syncZipAmount(currentText);
      });
    };

    state.priceObserver = new MutationObserver(updatePrice);
    state.priceObserver.observe(realPrice, {
      childList: true,
      characterData: true,
      subtree: true
    });

    return true;
  }

  function placeAfterpayWidget(mountId) {
    if (state.mountedId !== mountId) return false;

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    if (!priceWrapper) return false;

    let afterpayMount = document.querySelector('.cpb-afterpay-mount');
    if (!afterpayMount) {
      afterpayMount = document.createElement('div');
      afterpayMount.className = 'cpb-afterpay-mount';
      afterpayMount.style.display = 'block';
      afterpayMount.style.width = '100%';
      afterpayMount.style.marginTop = '8px';
      afterpayMount.style.flexBasis = '100%';
      afterpayMount.style.whiteSpace = 'normal';
      priceWrapper.insertAdjacentElement('afterend', afterpayMount);
    }

    const placement = document.querySelector('[data-cpb-afterpay-placement]');
    if (!placement) return false;

    if (placement.parentElement !== afterpayMount) {
      afterpayMount.appendChild(placement);
    }

    placement.style.display = 'block';

    const currentPriceEl = findBestPriceEl();
    if (currentPriceEl) syncAfterpayAmount(currentPriceEl.textContent);

    return true;
  }

  function moveDescriptionOnce(mountId) {
    const description = utils.qs('.product-block--description');
    const cpbDescription = utils.qs('.cpb-product-description');

    if (!description || !cpbDescription) return false;

    const move = () => {
      if (state.mountedId !== mountId) return;
      cpbDescription.replaceChildren(description);
    };

    const personalizeButton = utils.qs('.personalize-it--js');
    if (personalizeButton) {
      personalizeButton.addEventListener('click', move, { once: true });
    } else {
      move();
    }

    return true;
  }

  function placeElements(mountId) {
    if (state.mountedId !== mountId) return false;

    const productTitle = utils.qs('.cpb-product-title', state.panelsContainer);
    if (!productTitle) return false;

    const ratingStars = utils.qs('.product__information .klaviyo-star-rating-widget');

    ensureCustomPriceContainer();

    if (ratingStars && ratingStars.parentElement !== productTitle.parentElement) {
      productTitle.insertAdjacentElement('afterend', ratingStars);
    }

    if (state.customPricesContainer && !state.customPricesContainer.isConnected) {
      const target = ratingStars || productTitle;
      target.insertAdjacentElement('afterend', state.customPricesContainer);
    }

    return true;
  }

  function placeZipWidget(mountId) {
    if (state.mountedId !== mountId) return false;

    const priceWrapper = document.querySelector('.cpb-custom-product-price-container');
    const pricesContainer = document.querySelector('.cpb-custom-prices-container');
    if (!priceWrapper) return false;

    priceWrapper.style.display = 'block';
    priceWrapper.style.width = '100%';

    let zipMount = document.querySelector('.cpb-zip-widget-mount');
    if (!zipMount) {
      zipMount = document.createElement('div');
      zipMount.className = 'cpb-zip-widget-mount';
      zipMount.style.display = 'block';
      zipMount.style.width = '100%';
      zipMount.style.marginTop = '8px';
      zipMount.style.flexBasis = '100%';
      zipMount.style.whiteSpace = 'normal';
      if (pricesContainer) {
        pricesContainer.appendChild(zipMount);
      } else {
        priceWrapper.insertAdjacentElement('afterend', zipMount);
      }
    }

    let widget = document.getElementById('shopify-zip-prod-widget');
    if (!widget) {
      const store = getStoreContext();
      widget = document.createElement('quadpay-widget');
      widget.id = 'shopify-zip-prod-widget';
      widget.style.cursor = 'pointer';
      widget.dataset.zmAsset = 'productwidget';
      widget.dataset.zmWidget = 'popup';
      widget.dataset.zmPopupAsset = 'termsdialog';
      widget.dataset.zmRegion = store.region;
      widget.dataset.zmSymbol = store.symbol;
      widget.dataset.env = 'production';
      widget.dataset.zmPriceMin = '35';
      widget.dataset.zmPriceMax = '4000';
    }

    if (widget.parentElement !== zipMount) {
      zipMount.appendChild(widget);
    }

    const currentPriceEl = findBestPriceEl();
    if (currentPriceEl) syncZipAmount(currentPriceEl.textContent);

    try {
      if (window.Zip?.init) window.Zip.init();
      if (window.zmWidget?.init) window.zmWidget.init();
    } catch (_) {}

    return true;
  }

  const NAV_REGEX = /PREVIOUS|NEXT|RESET|PREVIOUS TAB|NEXT TAB/i;

  function removeNavigationButtons() {
    const buttons = utils.qsa('.cpb-product-actions button, .cpb-product-actions .cpb-actions-btn');
    if (!buttons.length) return false;

    let removed = 0;
    buttons.forEach(btn => {
      if (NAV_REGEX.test((btn.textContent || '').trim())) {
        btn.remove();
        removed++;
      }
    });

    return removed > 0;
  }

  function updateOutOfStockOverlays() {
    const wrappers = utils.qsa('.out-stock-wrapper');
    if (!wrappers.length) return false;

    wrappers.forEach(wrapper => {
      const overlay = utils.qs('.out-stock-overlay span', wrapper);
      const img = utils.qs('img', wrapper);
      if (!overlay || !img) return;

      const title = (img.alt || 'Product')
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .trim();

      overlay.textContent = `${title} - OUT OF STOCK`;
    });

    return true;
  }

  function init() {
    cleanup();
    state.mountedId = utils.newMountId();
    const mountId = state.mountedId;

    if (!initGlobals()) {
      utils.error('Product builder not found');
      return;
    }

    window.PXUTheme.contentCreator.accordion?.init?.();

    ensureCustomPriceContainer();

    utils.retry(() => moveDescriptionOnce(mountId));
    utils.retry(() => placeElements(mountId));
    utils.retry(() => placeAfterpayWidget(mountId));

    // Zip widget retries
    utils.retry(() => placeZipWidget(mountId));
    utils.setTrackedTimeout(() => placeZipWidget(mountId), 1200);
    utils.setTrackedTimeout(() => placeZipWidget(mountId), 2500);

    utils.retry(removeNavigationButtons);

    CONFIG.OOS_RETRY_DELAYS_MS.forEach(delay => {
      utils.setTrackedTimeout(() => updateOutOfStockOverlays(), delay);
    });
  }

  document.body.addEventListener('CPB_ON_PRODUCT_MOUNTED', init);
  window.addEventListener('pagehide', cleanup);
  window.addEventListener('beforeunload', cleanup);
})();
